import { ReactNode, useState } from "react";
import { parseISO } from "date-fns";
import { SWRConfig } from "swr";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { StatsCard } from "~/components/ui/stats-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "~/components/ui/toggle-group";
import { useIsDarkMode } from "~/hooks/useIsDarkMode";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { FundingSourceSection } from "~/pages/manifold-nextgen/components/projects/FundingSourceSection";
import { useProjectContext } from "~/pages/manifold-nextgen/components/projects/useProjectContext";
import { costLocalStorageProvider } from "~/utils/costSwrProvider";
import { CostChart } from "./CostChart";
import {
  type CostGranularity,
  costTypeLabel,
  type ProjectCostSummary,
  summarizeCostBreakdown,
} from "./costSummary";
import { costTypeColor, formatPeriod } from "./theme";
import { useProjectCostBreakdown } from "./useProjectCostBreakdown";

const PERIODS = [
  { months: 1, label: "This month" },
  { months: 3, label: "3 months" },
  { months: 6, label: "6 months" },
  { months: 12, label: "12 months" },
];
const DEFAULT_PERIOD_MONTHS = 6;

const GRANULARITIES: { value: CostGranularity; label: string }[] = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];
const DEFAULT_GRANULARITY: CostGranularity = "month";

const Panel = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="rounded-lg border border-border bg-card">
    <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-foreground">
      {title}
    </h2>
    <div className="p-4">{children}</div>
  </section>
);

const ShareBar = ({ share, color }: { share: string; color: string }) => (
  <span className="flex items-center gap-2">
    <span aria-hidden className="h-2 flex-1 rounded-sm bg-muted">
      <span className="block h-full rounded-sm" style={{ width: share, backgroundColor: color }} />
    </span>
    <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">{share}</span>
  </span>
);

const CostDashboard = ({
  summary,
  months,
  granularity,
}: {
  summary: ProjectCostSummary;
  months: number;
  granularity: CostGranularity;
}) => {
  const mode = useIsDarkMode() ? "dark" : "light";
  const currencyFormat = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: summary.currency,
  });
  const formatCost = (amount: number) => currencyFormat.format(amount);
  const share = (amount: number) =>
    `${(summary.total > 0 ? (amount / summary.total) * 100 : 0).toFixed(1)}%`;

  const periodCount = summary.periods.length;
  const peak = summary.periods.reduce((max, period) => (period.total > max.total ? period : max));
  const [largest] = summary.byType;
  const slotOf = (costType: string) => summary.costTypes.indexOf(costType);

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatsCard
          className="border"
          title={formatCost(summary.total)}
          description={months === 1 ? "Total this month" : `Total, last ${months} months`}
        />
        <StatsCard
          className="border"
          title={formatCost(summary.total / periodCount)}
          description={`Average per ${granularity}`}
        />
        <StatsCard
          className="border"
          title={formatCost(peak.total)}
          description={`Highest ${granularity}: ${formatPeriod(peak.periodStart, granularity)}`}
        />
        <StatsCard
          className="border"
          title={costTypeLabel(largest.costType)}
          description={`Largest cost type, ${share(largest.cost)} of spend`}
        />
      </div>

      <Panel title="Spend over time">
        <CostChart
          periods={summary.periods}
          granularity={granularity}
          costTypes={summary.costTypes}
          formatCost={formatCost}
        />
      </Panel>

      <Panel title="Cost by type">
        <Table aria-label="Cost by type">
          <TableHeader>
            <TableRow>
              <TableHead>Cost type</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="w-1/3">Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.byType.map(({ costType, cost }) => (
              <TableRow key={costType}>
                <TableCell>
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="size-2.5 shrink-0 rounded-sm"
                      style={{ backgroundColor: costTypeColor(slotOf(costType), mode) }}
                    />
                    {costTypeLabel(costType)}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatCost(cost)}</TableCell>
                <TableCell>
                  <ShareBar share={share(cost)} color={costTypeColor(slotOf(costType), mode)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>

      <Panel title="Cost by user">
        <Table aria-label="Cost by user">
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="w-1/3">Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.users.map(({ userId, userName, cost }) => (
              <TableRow key={userId ?? ""}>
                <TableCell>
                  {userId === null ? (
                    <span className="text-muted-foreground">Not attributed to a user</span>
                  ) : (
                    <span className="flex flex-col">
                      <span>{userName ?? userId}</span>
                      {userName && <span className="text-xs text-muted-foreground">{userId}</span>}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatCost(cost)}</TableCell>
                <TableCell>
                  <ShareBar share={share(cost)} color={costTypeColor(0, mode)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>

      <Panel title={`Spend by ${granularity}`}>
        <div className="max-h-96 overflow-y-auto">
          <Table aria-label={`Spend by ${granularity}`}>
            <TableHeader>
              <TableRow>
                <TableHead className="capitalize">{granularity}</TableHead>
                {summary.costTypes.map((costType) => (
                  <TableHead key={costType} className="text-right">
                    {costTypeLabel(costType)}
                  </TableHead>
                ))}
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...summary.periods].reverse().map(({ periodStart, total, byType }) => (
                <TableRow key={periodStart}>
                  <TableCell>{formatPeriod(periodStart, granularity)}</TableCell>
                  {summary.costTypes.map((costType) => (
                    <TableCell key={costType} className="text-right tabular-nums">
                      {formatCost(byType[costType] ?? 0)}
                    </TableCell>
                  ))}
                  <TableCell className="text-right font-medium tabular-nums">
                    {formatCost(total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Panel>

      <p className="text-xs text-muted-foreground">
        Cost data through{" "}
        {parseISO(summary.latestCostDate).toLocaleDateString("en-US", { dateStyle: "medium" })}.
      </p>
    </>
  );
};

const LoadingDashboard = () => (
  <div className="flex flex-col gap-6" aria-busy="true">
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {PERIODS.map(({ months }) => (
        <Skeleton key={months} className="h-20 rounded-lg" />
      ))}
    </div>
    <Skeleton className="h-80 rounded-lg" />
  </div>
);

const ProjectCostsContent = () => {
  const { project, isOwner } = useProjectContext();
  const [periodMonths, setPeriodMonths] = useState(DEFAULT_PERIOD_MONTHS);
  const [granularity, setGranularity] = useState<CostGranularity>(DEFAULT_GRANULARITY);
  const { data, error } = useProjectCostBreakdown(
    isOwner ? project.id : null,
    periodMonths,
    granularity
  );

  const renderBody = () => {
    if (!isOwner) {
      return (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Only project owners can view costs.
        </p>
      );
    }
    if (error) {
      return (
        <Alert variant="destructive">
          <AlertDescription>{"Couldn't load project costs."}</AlertDescription>
        </Alert>
      );
    }
    if (data === undefined) {
      return <LoadingDashboard />;
    }
    const summary = data && summarizeCostBreakdown(data);
    if (!summary || summary.byType.length === 0) {
      return (
        <p className="rounded-lg border border-border bg-card py-12 text-center text-sm text-muted-foreground">
          No costs recorded for this project in this period.
        </p>
      );
    }
    return <CostDashboard summary={summary} months={periodMonths} granularity={granularity} />;
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-4 pt-4 pb-6 sm:px-6">
        <TabHeader
          title="Costs"
          description="Spend attributed to this project: compute environments, workflows, AI agents, and storage."
          action={
            isOwner && (
              <div className="flex flex-wrap gap-2">
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  aria-label="Breakdown"
                  value={granularity}
                  onValueChange={(value) => {
                    const selected = GRANULARITIES.find((option) => option.value === value);
                    if (selected) {
                      setGranularity(selected.value);
                    }
                  }}
                >
                  {GRANULARITIES.map(({ value, label }) => (
                    <ToggleGroupItem key={value} value={value}>
                      {label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  aria-label="Period"
                  value={String(periodMonths)}
                  onValueChange={(value) => value && setPeriodMonths(Number(value))}
                >
                  {PERIODS.map(({ months, label }) => (
                    <ToggleGroupItem key={months} value={String(months)}>
                      {label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            )
          }
        />
        {isOwner && <FundingSourceSection workbenchId={project.workbench_id} />}
        {renderBody()}
      </div>
    </div>
  );
};

/** Owner-only cost dashboard for a project, from the cost-and-usage service. */
export const ProjectCosts = () => (
  <SWRConfig value={{ provider: costLocalStorageProvider }}>
    <ProjectCostsContent />
  </SWRConfig>
);
